import React from 'react';
import moment from 'moment';
import { translate } from 'react-i18next';
import Button from '../layout/Button';
import Toggle from '../forms/Toggle';
import BrightnessTabs from '../profiles/BrightnessTabs';
import BrightnessGraph from '../profiles/BrightnessGraph';
import { Icon } from '../icons';
import './profile.scss';

import { relativeTimeConverter } from '../../utils';

const findNextFreeSlot = (convertedTimes, requestedSlot) => {
  if (convertedTimes.length === 0) {
    return "12:00"
  }
  if(convertedTimes.map(t => t.time.format("HH:mm")).indexOf(requestedSlot.format("HH:mm")) === -1) {
    return requestedSlot.format("HH:mm");
  } else {
    return findNextFreeSlot(convertedTimes, requestedSlot.clone().add(1, 'hour'))
  }
}

const availableSlots = [];
for (let i=0; i < 24; i++) {
  for (let j=0; j < 4; j++) {
    availableSlots.push(`${i < 12 ? `${i + 12}`.padStart(2, "0") : `${i - 12}`.padStart(2, "0")}:${`${j * 15}`.padStart(2, "0")}`)
  }
}

const TimeSelect = ({ value, onChange, disabledValues }) => {
  return (
    <div>
      <select value={value} onChange={onChange}>
        {availableSlots.map(s => <option disabled={disabledValues.indexOf(s) !== -1} key={s} value={s}>{s}</option>)}
      </select>
    </div>
  )
}

const TimeSlot = ({ day, brightness, onChange, onRemove, removable, occupiedSlots }) => (
  <div className="profile-form-grid">
    <div className="timeslot-timepicker">
      { brightness.time.format("HH:mm") === "12:00" ? <div className="required-timeslot">{brightness.time.format("HH:mm")}</div> : (
        <TimeSelect disabledValues={occupiedSlots} value={brightness.time.format("HH:mm")} onChange={e => { onChange(day, brightness.time.format("HH:mm"), 'time', e.target.value) }}/>
      )}
    </div>
    <div className="timeslot-dim-low">
      <input type="number" value={brightness.low} onChange={(e) => { if(parseInt(e.target.value)>=0 && parseInt(e.target.value)<=100) return onChange(day, brightness.time.format("HH:mm"), 'low', parseInt(e.target.value))}} name="low" />
    </div>
    <div className="timeslot-dim-high">
      <input type="number" value={brightness.high} onChange={(e) =>{ if(parseInt(e.target.value)>=0 && parseInt(e.target.value)<=100) return onChange(day, brightness.time.format("HH:mm"), 'high', parseInt(e.target.value))}} name="high" />
    </div>
    <div className="timeslot-remove">
      {removable && (
        <Button onClick={onRemove}>
          <Icon type={'bin'} className="timeslot-remove-icon"/>
        </Button>)
      }
    </div>
  </div>
)

export const DaySlot = ({ t, day, brightness, onTimeSlotUpdate, onTimeSlotRemove, onTimeSlotAdd }) => {
  const convertedTimes = brightness.map(relativeTimeConverter).sort((a, b) => { return a.time - b.time });
  return (
    <div className="profile-form-container">
      { brightness.length > 0 && (
        <div className="profile-form-grid">
          <div className="timeslot-time-header">{t('TIME')}</div>
          <div className="timeslot-dim-low-header">{t('LOW')}</div>
          <div className="timeslot-dim-high-header">{t('HIGH')}</div>
          <div className="timeslot-remove-header"></div>
        </div>
      )}
      {convertedTimes.map(time_slot =>
        <TimeSlot
          day={day}
          key={time_slot.time.format("HH:mm")}
          brightness={time_slot}
          removable={time_slot.time.format("HH:mm") !== "12:00" || (brightness.length == 1 && day !== "default")}
          onRemove={() => onTimeSlotRemove(day, time_slot.time.format("HH:mm"))}
          onChange={onTimeSlotUpdate}
          occupiedSlots={convertedTimes.map(t => t.time.format("HH:mm"))} />
      )}

      {brightness.length < 6 && (
        <Button className="timeslot-new" onClick={() => onTimeSlotAdd(day, convertedTimes.length === 0 ? "12:00" : findNextFreeSlot(convertedTimes, convertedTimes[convertedTimes.length -1].time.clone().add(1, 'hour')), 40, 100)}>
          {t('ADD_TIMESLOT')}
        </Button>
      )}
    </div>
  )
}

const Profile = ({ t, profile, errors, onChange, onTimeSlotUpdate, onTimeSlotRemove, onTimeSlotAdd, activeBrightnessTab, onBrightnessTabChange }) =>
  <form className="profile-form">
    {errors.full_messages.length > 0 &&
      <ul className="red bordered">
        {errors.full_messages.map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    }
    <div className="profile-input-fields">
      <div className="form-group form-input">
        <label htmlFor="name">{t('NAME')}</label>
        <input type="text" name="name" value={profile.name} onChange={onChange}/>
      </div>
      <div className="form-group form-input-suffix">
        <label htmlFor="hold_time">{t('HOLD_TIME')}</label>
        <input type="text" value={profile.hold_time} onChange={onChange} name="hold_time" />
        <span className="unit">{t('SECONDS')}</span>
      </div>
      <div className="form-group form-input-suffix">
        <label htmlFor="sensor_off_time">{t('SENSOR_OFF_TIME')}</label>
        <input type="text" value={profile.sensor_off_time} onChange={onChange} name="sensor_off_time" />
        <span className="unit">{t('SECONDS')}</span>
      </div>
      <div className="form-group form-input-suffix">
        <label htmlFor="light_rate_up">{t('LIGHT_RATE_UP')}</label>
        <input type="text" value={profile.light_rate_up} onChange={onChange} name="light_rate_up" />
        <span className="unit">{t('SECONDS')}</span>
      </div>
      <div className="form-group form-input-suffix">
        <label htmlFor="light_rate_down">{t('LIGHT_RATE_DOWN')}</label>
        <input type="text" value={profile.light_rate_down} onChange={onChange} name="light_rate_down" />
        <span className="unit">{t('SECONDS')}</span>
      </div>
      <div className="form-group form-input-suffix">
        <label htmlFor="fallback_level">{t('FALLBACK_LEVEL')}</label>
        <input type="text" value={profile.fallback_level} onChange={onChange} name="fallback_level" />
        <span className="unit">%</span>
      </div>
      <div className="form-group form-input-toggle">
        <div className="label">
          {t('CENTRAL_ALS')}
        </div>
        <Toggle checked={profile.central_als_mode_enabled} name={"central_als_mode_enabled"} onChange={onChange} />
      </div>
      <div className="form-group form-input-toggle">
        <div className="label">
          {t('ASTRO_CLOCK')}
        </div>
        { profile.central_als_mode_enabled === true ? <Toggle checked={profile.astro_clock = true} name={"astro_clock"} onChange={onChange} /> : (
          <Toggle checked={profile.astro_clock} name={"astro_clock"} onChange={onChange} />
        )}
      </div>
      { profile.astro_clock && (
        <div className="form-group form-input-suffix" >
          <label htmlFor="astro_offset_sunset">{t('OFFSET_SUNSET')}</label>
          <input type="text" value={profile.astro_offset_sunset} onChange={onChange} name="astro_offset_sunset" />
          <span className="unit">{t('MINUTES')}</span>
        </div>
      )}
      { profile.astro_clock && (
        <div className="form-group form-input-suffix" >
          <label htmlFor="astro_offset_sunrise">{t('OFFSET_SUNRISE')}</label>
          <input type="text" value={profile.astro_offset_sunrise} onChange={onChange} name="astro_offset_sunrise" />
          <span className="unit">{t('MINUTES')}</span>
        </div>
      )}
      <div className="form-group form-input-toggle">
        <div className="label">
          {t('LIGHT_SENSOR')}
        </div>
        <Toggle checked={profile.als_mode_enabled} name={"als_mode_enabled"} onChange={onChange} />
      </div>
      { profile.als_mode_enabled && (
        <div className="form-group form-input-suffix" >
          <label htmlFor="als_lux_on">{t('LIGHT_SENSOR_ON')}</label>
          <input type="text" value={profile.als_lux_on || ""} onChange={onChange} name="als_lux_on" />
          <span className="unit">{t('LUX')}</span>
        </div>
      )}
    </div>

    <div className="profile-graph">
      <BrightnessTabs activeTab={activeBrightnessTab} brightness={profile.brightness} onChange={onBrightnessTabChange} />
      <div className="tab-content">
        <DaySlot t={t} day={activeBrightnessTab} brightness={profile.brightness[activeBrightnessTab] || []} onTimeSlotUpdate={onTimeSlotUpdate} onTimeSlotRemove={onTimeSlotRemove} onTimeSlotAdd={onTimeSlotAdd} />
        {profile.brightness[activeBrightnessTab] && <BrightnessGraph brightness={profile.brightness[activeBrightnessTab] || []} />}
      </div>
    </div>
  </form>

export default translate('profile')(Profile);



// WEBPACK FOOTER //
// ./src/components/forms/Profile.js